<template>
  <v-card>
    <v-card-title class="report-title">
        <v-col col="12" md="6">
          <h4>{{ `${$t('report.time_period')}: ${periodStartName} - ${periodEndName}` }}</h4>
        </v-col>
        <v-col col="12" md="6" class="text-right">
          <h3>{{ report.report_name }}</h3>
          <h5 v-if="report.location" class="body-1">{{ report.location.address }}</h5>
        </v-col>
    </v-card-title>
    <v-card-text>
      <v-card
        v-for="(item, key) in reportItems"
        :key="key"
        :elevation="key === 0 ? 4 : 1"
      >
        <v-container pa-3 :mb-2="key !== 0" :mb-4="key === 0"
                     v-if="typeof item.topic.show === 'undefined' || item.topic.show">
          <v-layout
            row
            wrap
            :class="[{'topic-header-with-value': item.topic.value !== undefined}, item.topic.type]"
          >
              <v-col cols="10" class="py-1">
                <h3 class="topic-header primary--text">{{ item.topic.header }}</h3>
                <span class="hint">{{ item.topic.hint }}</span>
              </v-col>
              <v-col cols="2" class="py-1">
                <span>{{ item.topic.value }}</span>
              </v-col>
          </v-layout>

          <v-layout
            row
            wrap
            v-for="(entry,key) in item.entries"
            @click="showDetails(entry.details)"
            :class="['report-entry', entry.type]"
            :key="key"
          >
            <template v-if="typeof entry.show === 'undefined' || entry.show">
              <v-col cols="10" class="py-1">
                <span>{{ entry.header }}</span>
                <span class="hint">{{ entry.hint }}</span>
              </v-col>
              <v-col cols="2" class="py-1">
                <span>{{ entry.value || '-' }}</span>
              </v-col>
            </template>
          </v-layout>
        </v-container>
      </v-card>
    </v-card-text>
    <v-dialog v-model="isShowDetails" max-width="650">
      <sales-summary :sales-summary="salesSummary" :details-loading="detailsLoading"></sales-summary>
    </v-dialog>
  </v-card>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'

import reportService from '@/services/report'

import SalesSummary from '@/components/location/SalesSummary'

export default {
  components: {
    SalesSummary
  },

  props: {
    report: Object
  },

  data () {
    return {
      isShowDetails: false,
      salesSummary: [],
      detailsLoading: false
    }
  },

  computed: {
    lastPeriodName () {
      if (!this.report.period) {
        return ''
      }

      return moment(this.report.period, 'YYYYMM').subtract({ month: 1 }).format('YYYY MMM')
    },

    periodStartName () {
      if (!this.report.period_start) {
        return ''
      }

      return moment.unix(this.report.period_start).format('MM/DD/YYYY')
    },

    periodEndName () {
      if (!this.report.period_end) {
        return ''
      }

      return moment.unix(this.report.period_end).format('MM/DD/YYYY')
    },

    reportItems () {
      let totalCostAndExpense = _.round(+this.report.cost_product + +this.report.cost_rent + (+this.report.cost_replenishment || 0) + +this.report.cost_transaction + +this.report.cost_internet, 2)
      let totalRevenue = _.round(+this.report.income_sales + +this.report.income_other + +this.report.income_rental + +this.report.income_rent_subsidy, 2)
      let profit = _.round(totalRevenue - totalCostAndExpense, 2)

      return [
        // Account Balance
        {
          topic: {
            header: this.$t('report.account_balance'),
            value: this.report.balance_current,
            type: 'subtotal'
          },
          entries: [
            {
              header: this.$t('report.total_proceeds_with_period', { period: this.report.period_name }),
              value: this.report.total_proceed
            },
            {
              header: this.$t('report.balance_brought_forward', { period: this.lastPeriodName }),
              value: this.report.balance_brought_forward
            },
            {
              header: this.$t('report.withdrawal', { period: this.report.period_name || '/' }),
              value: `-${this.report.balance_withdrawn}`,
              show: this.report.balance_withdrawn > 0,
              type: 'subtotal'
            },
            {
              header: this.$t('report.adjustment_total') + (this.report.adjustment_total_remark ? ' (' + this.report.adjustment_total_remark + ')' : ''),
              value: `${this.report.adjustment_total}`,
              show: this.report.adjustment_total !== 0,
              type: 'subtotal'
            }
          ]
        },
        // Revenue
        {
          topic: {
            header: this.$t('report.revenue')
          },
          entries: [
            {
              header: this.$t('report.merchandise_sales'),
              value: this.report.income_sales,
              details: 'sales-summary',
              type: 'clickable',
              show: this.report.income_sales || !this.report.income_rental
            },
            {
              header: this.$t('report.income_rental'),
              value: this.report.income_rental,
              show: this.report.income_rental
            },
            {
              header: this.$t('report.income_rent_subsidy'),
              value: this.report.income_rent_subsidy,
              show: this.report.income_rent_subsidy
            },
            {
              header: this.$t('report.other_revenue'),
              value: this.report.income_other,
              show: this.report.income_other || !this.report.income_rental
            },
            {
              header: `${this.$t('report.total_revenue')} (A)`,
              value: totalRevenue,
              type: 'subtotal'
            }
          ]
        },
        // Cost & Expenses
        {
          topic: {
            header: this.$t('report.cost_and_expenses')
          },
          entries: [
            {
              header: this.$t('report.cost_of_goods_sold'),
              value: this.report.cost_product
            },
            {
              header: this.$t('report.cost_replenishment'),
              value: this.report.cost_replenishment
            },
            {
              header: this.$t('report.rent'),
              hint: this.report.rent_clause,
              value: this.report.cost_rent
            },
            {
              header: this.$t('report.payment_fee'),
              hint: this.$t('report.payment_fee_hint', { rate: '1.6% - 5' }),
              value: this.report.cost_transaction
            },
            {
              header: this.$t('report.internet'),
              value: this.report.cost_internet
            },
            {
              header: `${this.$t('report.total_cost_and_expenses')} (B)`,
              value: totalCostAndExpense,
              type: 'subtotal'
            }
          ]
        },
        // Net Profit
        {
          topic: {
            header: `${this.$t('report.net_profit')} (A - B)`,
            value: profit,
            type: 'subtotal'
          }
        },
        // Profit Share
        {
          topic: {
            header: this.$t('report.profit_share')
          },
          entries: [
            {
              header: 'Gritus',
              hint: this.$t('report.profit_share_hint', { rate: (100 - this.report.franchisee_share) || '' }),
              value: this.report.profit_gritus,
              type: 'subtotal'
            },
            {
              header: `${this.$t('report.franchisee')} (C)`,
              hint: this.$t('report.profit_share_hint', { rate: this.report.franchisee_share }),
              value: this.report.profit_franchisee,
              type: 'subtotal'
            }
          ]
        },
        // Guaranteed Clause
        {
          topic: {
            header: this.$t('report.guaranteed_clause'),
            show: this.report.adjustment_guarantee
          },
          entries: [
            {
              header: `${this.$t('report.guaranteed_clause_proceed')} (D)`,
              value: this.report.adjustment_guarantee,
              type: 'subtotal'
            }
          ]
        },
        // Management Fee
        {
          topic: {
            header: this.$t('report.management_fee'),
            show: !this.report.income_rental && this.report.adjustment_management > 0
          },
          entries: [
            {
              header: `Gritus ${this.$t('report.management_fee')} (E)`,
              hint: this.$t('report.gritus_management_fee_hint', { amount: '1000' }),
              value: this.report.adjustment_management,
              type: 'subtotal'
            }
          ]
        },
        // Total Proceeds
        {
          topic: {
            header: `${this.$t('report.total_proceeds')} (C + D - E)`
          },
          entries: [
            {
              header: this.$t('report.total_proceeds_with_period', { period: this.report.period_name }),
              value: this.report.total_proceed,
              type: 'subtotal'
            }
          ]
        }
      ]
    }
  },

  watch: {
    report () {
      this.loadDetails()
    }
  },

  created () {
    this.loadDetails()
  },

  methods: {
    async loadDetails () {
      this.detailsLoading = true
      try {
        let { data } = await reportService.getSalesDetails(this.report.id)
        this.salesSummary = data.data.sales_summary
      } finally {
        this.detailsLoading = false
      }
    },

    showDetails (details) {
      if (typeof details === 'undefined') {
        return
      }

      this.isShowDetails = true
    }
  }
}
</script>

<style lang="stylus" scoped>
  .report-title
    padding-bottom: 0px

  .topic-header
    display: inline

  .hint
    margin-left: 10px

  .report-entry:hover, .topic-header-with-value:hover
    background-color: LightGrey

  .report-remarks
    margin-top: 20px

  .subtotal
    font-weight: bold

  .clickable
    cursor: pointer
    text-decoration: underline
    color: var(--v-primary-base)
</style>
